<script setup lang="ts">
    import { inject, onMounted, reactive, ref, watch  } from 'vue';
    import axios from 'axios';
    import { useRoute, useRouter } from 'vue-router';
    import CustomUploadInput from '@/components/CustomUploadInput.vue';

    const router = useRouter();
    const route = useRoute();
    const BACKEND_URL = inject('BACKEND_URL');
    const errorMessage = ref('');
    const loading = ref(false);
    const isFormValid = ref(false);
    const startupFormDatas = reactive({ 
        roles: [] as any[],
        admin: undefined
    });
    const AVATARS_LIST = ['user.jpg', 'user1.jpg', 'user2.jpg', 'user3.jpg', 'user4.jpg', 'user5.jpg', 'user6.jpg', 'user7.jpg', 'user8.jpg', 'user9.jpg', 'user10.jpg', 'user11.jpg'];
    const formData = reactive({
        id: null,
        avatar: AVATARS_LIST.length > 0 ? AVATARS_LIST[0] : '',
        role_id: null,
        username: '',
        email: '',
        etat_valide: true,
        photo: null as any,
        photo_preview: '/assets/users/default.png',
        sexe: 'M',
        nom: '',
        prenom: '',
        fonction: '',
        telephone: ''
    });
    const errors = reactive({
        role_id: '',
        username: '',
        email: '',
        nom: '',
        prenom: '',
        telephone: '',
    });

    onMounted(() => {
        const startupFormInfos: any = route.meta.form_datas;
        startupFormDatas.roles = startupFormInfos.roles;
        startupFormDatas.admin = startupFormInfos.admin;
        if (startupFormInfos.admin !== undefined) {
            formData.id = startupFormInfos.admin.id;
            formData.avatar = startupFormInfos.admin.avatar;
            formData.role_id = startupFormInfos.admin.role_id;
            formData.username = startupFormInfos.admin.username;
            formData.email = startupFormInfos.admin.email;
            formData.etat_valide = startupFormInfos.admin.etat_valide;
            if (startupFormInfos.admin.photo_url) {
                formData.photo_preview = startupFormInfos.admin.photo_url;
            }
            formData.sexe = startupFormInfos.admin.sexe;
            formData.nom = startupFormInfos.nom && startupFormInfos.nom != 'null' ? startupFormInfos.nom : '';
            formData.prenom = startupFormInfos.prenom && startupFormInfos.prenom != 'null' ? startupFormInfos.prenom : '';
            formData.fonction = startupFormInfos.fonction && startupFormInfos.fonction != 'null' ? startupFormInfos.fonction : '';
            formData.telephone = startupFormInfos.telephone && startupFormInfos.telephone != 'null' ? startupFormInfos.telephone : '';
        }
    });

    watch(formData, (oldFormData, newFormData) => {
        validate();
    });

    function doSelectAvatar(avatar: string) {
        formData.avatar = avatar;
    }

    function validatePhone(phoneValue: string) {
        // Exemple pour format international commençant par + (ex: +2250700000000)
        const regex = /^\+?[0-9]{8,15}$/;
        return regex.test(phoneValue);
    }

    function validateEmail(emailValue: string) {
        // Exemple pour format international commençant par + (ex: +2250700000000)
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(emailValue);
    }

    function validate() {
        let isValid = true;

        errors.role_id = '';
        if (!formData.role_id) {
            errors.role_id ="Le role est requis";
            isValid = false;
        }

        errors.username = '';
        if (!formData.username) {
            errors.username ="Le nom d'utilisateur est requis";
            isValid = false;
        }

        errors.email = '';
        if (!validateEmail(formData.email)) {
            errors.email ="L'adresse email est invalide";
            isValid = false;
        }

        /*errors.nom = '';
        if (!formData.nom) {
            errors.nom ="Le nom est requis";
            isValid = false;
        }

        errors.prenom = '';
        if (!formData.prenom) {
            errors.prenom ="Le prénom est requis";
            isValid = false;
        }*/

        errors.telephone = '';
        if (formData.telephone && !validatePhone(formData.telephone)) {
            errors.telephone = 'Le téléphone est invalide';
            isValid = false;
        }

        isFormValid.value = isValid;
        
        return isValid;
    }

    function onFileUploaded(uploadedFile: { file: any, file_name: string, file_preview: string }) {
        formData.photo_preview = uploadedFile.file_preview;
        formData.photo = { file: uploadedFile.file, name: uploadedFile.file_name };
    }

    async function onSubmit() {
        if (!validate()) { return; }
        loading.value = true;
        errorMessage.value = '';
        try {

            const formToUpload = new FormData();
            if (formData.id != null) {
                formToUpload.append("id", `${formData.id}`);
            }
            formToUpload.append("avatar", formData.avatar);
            formToUpload.append("role_id", `${formData.role_id}`);
            formToUpload.append("username", formData.username);
            formToUpload.append("email", formData.email);
            formToUpload.append("etat_valide", `${formData.etat_valide ? 1 : 0}`);

            if (formData.photo?.file) {
                formToUpload.append("photo", formData.photo.file, formData.photo.name);
            }

            formToUpload.append("sexe", formData.sexe);
            formToUpload.append("nom", formData.nom);
            formToUpload.append("prenom", formData.prenom);
            formToUpload.append("fonction", formData.fonction);
            formToUpload.append("telephone", formData.telephone);

            await axios.post(`/backoffice/admins/save`, formToUpload);
            router.push('/app/admins/list');
        } catch(error: any) {
            errorMessage.value = error.message;
            console.error(error);
        } finally {
            loading.value = false;
        }
    }
</script>

<template>
<section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <form class="form widget flex container-full-3 py-8 pb-10">
            <section class="s9_mobile">
                <h4 class="font-semibold mb-5">Administrateur / Création <span class="text-sm text-grey-500"></span></h4>

                <div class="grid6 grid-row-gap-1">
                    <div class="rounded-100 overflow-hidden cursor-pointer item" @click="doSelectAvatar(avatar)" :class="{'active': formData.avatar == avatar }" v-for="(avatar, index) in AVATARS_LIST" :key="'avatar_' + index"><!--active-->
                        <img :src="'/assets/users/300w/' + avatar" width="100%" />
                    </div>
                </div>

                <h5 class="font-semibold my-5">Connexion <span class="text-sm text-grey-500"></span></h5>

                <div class="form-field-floating active my-4">
                    <select class="form-input-select" name="role_id" id="role_id" v-model="formData.role_id">
                        <option value="" selected>Sélectionnez le rôle</option>
                        <option :value="role.id" v-for="(role, index) in startupFormDatas.roles" :key="'role_' + index">
                            {{ role.libelle }}
                        </option>
                    </select>
                    <label for="role">Rôle <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['role_id']" class="text-red">{{ errors['role_id'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.username" name="username" id="username" placeholder="">
                    <label for="username">Identifiant <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['username']" class="text-red">{{ errors['username'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="email" class="form-input" v-model="formData.email" name="email" id="email" placeholder="">
                    <label for="email">Email <b class="color-danger">*</b></label>
                </div>
                <span v-if="!!errors['email']" class="text-red">{{ errors['email'] }}</span>

                <label class="form-switch lg mt-6 mb-3">
                    <div class="switch">
                        <input type="checkbox" name="etat_valide" :v-model="formData.etat_valide" :checked="formData.etat_valide" />
                        <span class="slider round"></span>
                    </div>
                    <h6 class="label text-grey-600">Compte actif</h6>
                </label>
            </section>

            <div class="s1_mobile flex-center">
                <hr class="hr-vertical h-90p"/>
            </div>

            <section class="s10_mobile">
                <div class="h-20"></div>
                <div class="custom-upload-size margin-auto flex mb-8">
                    <custom-upload-input id="photo" label="Photo de profil" :preview="formData.photo_preview" type="IMAGE" @uploaded="onFileUploaded"></custom-upload-input>
                </div>

                <h5 class="font-semibold mb-5">Informations de l'Administrateur <span class="text-sm text-grey-500"></span></h5>

                <div class="grid2">
                    <div class="card-check mb-2" :class="{ 'active': formData.sexe == 'M' }"><!-- color active sm single -->
                        <label class="form-check-label" for="sexe_M">
                            <div class="card-check-icon"><img src="/icons/icon-male.svg" /></div>
                            <div class="card-check-text">
                                <input class="form-check-input" type="radio" name="sexe" id="sexe_M" value="M" v-model="formData.sexe" />
                                <h6>Je suis un homme</h6>
                                <span>Masculin</span>
                            </div>  
                        </label>
                    </div>
                    <div class="card-check mb-2" :class="{ 'active': formData.sexe == 'F' }"><!-- color active sm single -->
                        <label class="form-check-label" for="sexe_F">
                            <div class="card-check-icon"><img src="/icons/icon-female.svg" /></div>
                            <div class="card-check-text">
                                <input class="form-check-input" type="radio" name="sexe" id="sexe_F" value="F" v-model="formData.sexe" />
                                <h6>Je suis une femme</h6>
                                <span>Féminin</span>
                            </div>  
                        </label>
                    </div>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.nom" name="nom" id="nom" placeholder="">
                    <label for="nom">Nom</label>
                </div>
                <span v-if="!!errors['nom']" class="text-red">{{ errors['nom'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.prenom" name="prenom" id="prenom" placeholder="">
                    <label for="prenom">Prénom</label>
                </div>
                <span v-if="!!errors['prenom']" class="text-red">{{ errors['prenom'] }}</span>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.fonction" name="fonction" id="fonction" placeholder="">
                    <label for="fonction">Fonction</label>
                </div>

                <div class="form-field-floating reactive my-4">
                    <input type="text" class="form-input" v-model="formData.telephone" name="telephone" id="telephone" placeholder="">
                    <label for="telephone">Téléphone</label>
                </div>
                <span v-if="!!errors['telephone']" class="text-red">{{ errors['telephone'] }}</span>

                <div class="clear-both h-25"></div>
                <span v-if="!!errorMessage" class="text-red">{{ errorMessage }}</span>
                <p class="text-xs text-grey-500 mb-5"><b class="color-danger text-xs">*</b> Les champs marqués d'un astérisque sont obligatoires</p>
                <div class="flex gap-2">
                    <custom-button id="btn-submit" type="button" :classesList="'btn lg w-40'" :loading="loading" @click="onSubmit()">
                        <img src="/icons/icon-enregistrer.svg" class="btn-icon" alt="" />
                        <span>Enregistrer</span>
                    </custom-button>
                    <button class="btn stroke black lg px-10">
                        <span>Effacer</span>
                    </button>
                </div>
            </section>
        </form>
    </section>
</template>
